import React from 'react';
import { Link } from 'react-router-dom';
import BlogArticleTemplate from '../../components/BlogArticleTemplate';

const WhenToRefinance = () => (
  <BlogArticleTemplate
    title="When Should You Refinance Your Home Loan?"
    description="Learn how to identify the right time to refinance, calculate your break-even point, and understand whether switching lenders could save you money."
    path="/blog/when-to-refinance"
    author="Manage Your Loans Team"
    date="2026-03-10"
    readTime={10}
    faqs={[
      {
        q: 'How do I know if it is the right time to refinance?',
        a: 'Consider refinancing if your current rate is noticeably higher than what is available in the market, your fixed rate term is about to expire, your property has increased in value (which may reduce your LVR and unlock a better rate), or your financial circumstances have changed and you need different loan features. A mortgage broker can run a comparison to show whether the savings outweigh the costs of switching.',
      },
      {
        q: 'What costs are involved in refinancing a home loan?',
        a: 'Common refinancing costs include a discharge fee from your current lender (typically $150 to $400), government registration fees for the new mortgage, and potentially a valuation fee. Some lenders charge an application or settlement fee for the new loan, while others waive these to attract refinancers. If you are on a fixed rate, you may also face break costs, which can be substantial.',
      },
      {
        q: 'How long does the refinancing process take?',
        a: 'A straightforward refinance typically takes two to four weeks from application to settlement. Complex applications (such as those involving self-employed income or investment properties) may take longer. Your broker manages the process and coordinates between your current and new lender to minimise delays. All loan documents can be signed digitally via SignAndGo.',
      },
      {
        q: 'Can I refinance if I have less than 20% equity?',
        a: 'Yes, but you may need to pay Lenders Mortgage Insurance (LMI) on the new loan if your loan-to-value ratio exceeds 80%. Some lenders offer LMI waivers for certain professions (such as doctors, lawyers, and accountants). Your broker can identify whether refinancing still makes financial sense after factoring in LMI.',
      },
    ]}
  >
    <p className="text-lg text-gray-700 leading-relaxed mb-8">
      Refinancing your home loan means replacing your existing mortgage with a new
      one, typically with a different lender or on different terms. Done at the
      right time and for the right reasons, refinancing can save you tens of
      thousands of dollars over the life of your loan. Done poorly, it can cost you
      money. This guide helps you figure out which situation applies to you.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      Common Reasons to Refinance
    </h2>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      1. Securing a Lower Interest Rate
    </h3>
    <p className="text-gray-700 leading-relaxed mb-4">
      This is the most common reason borrowers refinance. Even a small rate
      reduction can make a meaningful difference. On a $500,000 loan with 25 years
      remaining, reducing your rate by 0.50% could save you approximately $150 per
      month or over $45,000 in total interest (the exact figure depends on your
      specific loan terms and repayment type).
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      Lenders often reserve their most competitive rates for new customers, which
      means loyal borrowers may be paying more than necessary. This is sometimes
      called the "loyalty tax." If you have not reviewed your rate in the past 12
      to 18 months, it is worth checking where you stand compared to the current
      market.
    </p>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      2. Accessing Equity
    </h3>
    <p className="text-gray-700 leading-relaxed mb-4">
      If your property has increased in value since you purchased it, you may have
      built up equity that you can access through refinancing. This equity can be
      used for renovations, purchasing an investment property, or other purposes.
      Your broker can help you understand how much usable equity you have and
      structure the loan appropriately.
    </p>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      3. Changing Loan Features
    </h3>
    <p className="text-gray-700 leading-relaxed mb-4">
      Your needs may have evolved since you first took out your loan. Perhaps you
      want an offset account, the ability to make extra repayments without penalty,
      a redraw facility, or the flexibility to split your loan between fixed and
      variable portions. Refinancing lets you switch to a product that better fits
      your current circumstances.
    </p>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      4. Consolidating Debts
    </h3>
    <p className="text-gray-700 leading-relaxed mb-4">
      Some borrowers refinance to consolidate higher-interest debts (such as credit
      cards or personal loans) into their mortgage. While this can reduce your
      overall monthly payments, be aware that you are spreading these debts over a
      longer term, which may result in paying more interest overall. Discuss the
      trade-offs with your broker before proceeding.
    </p>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      5. Fixed Rate Expiry
    </h3>
    <p className="text-gray-700 leading-relaxed mb-6">
      When a fixed rate term expires, your loan typically reverts to the lender's
      standard variable rate, which is often higher than what you could obtain by
      refinancing to a new product. This is one of the most common triggers for
      refinancing and a good time to review all your options.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      Calculating Your Break-Even Point
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      Refinancing is not free, so you need to ensure the savings outweigh the
      costs. The break-even point is the number of months it takes for your
      monthly savings to recoup the upfront costs of refinancing.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      <strong>Example:</strong> If refinancing costs you $1,500 in total (discharge
      fees, registration, and application fees) and saves you $200 per month in
      repayments, your break-even point is 7.5 months. After that, every month's
      savings is money in your pocket.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      If the break-even point is more than two to three years away, refinancing may
      not be worthwhile unless you plan to hold the loan for significantly longer.
      Your broker can calculate this precisely based on your current loan details
      and the proposed new loan terms.
    </p>

    <div className="bg-gray-50 rounded-xl p-6 my-8 border border-gray-100">
      <h4 className="font-semibold text-[#00594C] mb-3">Quick Break-Even Formula</h4>
      <p className="text-gray-700 mb-2">
        <strong>Break-even (months)</strong> = Total refinancing costs / Monthly savings
      </p>
      <p className="text-sm text-gray-500">
        If the result is under 12 months, refinancing is generally a strong financial
        decision. Between 12 and 24 months is still favourable for most borrowers.
      </p>
    </div>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      When Refinancing May Not Make Sense
    </h2>
    <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
      <li className="leading-relaxed">
        <strong>You are on a fixed rate with high break costs.</strong> Exiting a
        fixed rate early can incur substantial break costs (sometimes tens of
        thousands of dollars). Always get a break cost estimate from your current
        lender before proceeding.
      </li>
      <li className="leading-relaxed">
        <strong>Your loan balance is small.</strong> On smaller loan balances, the
        dollar savings from a lower rate may not justify the costs and effort of
        refinancing.
      </li>
      <li className="leading-relaxed">
        <strong>You are planning to sell soon.</strong> If you intend to sell your
        property within the next year or two, the refinancing costs may not be
        recovered in time.
      </li>
      <li className="leading-relaxed">
        <strong>Your credit profile has deteriorated.</strong> If your financial
        circumstances have changed for the worse since you took out your original
        loan, you may not qualify for a better rate elsewhere.
      </li>
    </ul>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      The Refinancing Process
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      Refinancing follows a similar process to taking out a new home loan. Your
      broker will review your current loan, compare it against available products,
      and recommend options. If you decide to proceed, the broker manages the
      application, coordinates with both lenders, and handles the settlement.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      At Manage Your Loans, we make refinancing as straightforward as possible.
      All loan documents are signed securely via SignAndGo, meaning you can
      complete the process from home. We also continue monitoring your rate after
      settlement to ensure it remains competitive.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      The typical timeline from initial enquiry to settlement is two to four weeks
      for a straightforward refinance. More complex scenarios, such as those
      involving self-employed borrowers or multiple properties, may take slightly
      longer.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      How Often Should You Review Your Loan?
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      We recommend reviewing your home loan at least once a year, or whenever
      there is a significant change in your circumstances (such as a salary
      increase, change in family situation, or property value growth). Regular
      reviews ensure you are not paying more than you need to.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      If you are unsure whether refinancing is right for you,{' '}
      <Link to="/refinance" className="text-[#00594C] font-semibold underline hover:text-[#99DF63] transition-colors">
        explore our refinancing services
      </Link>{' '}
      or{' '}
      <Link to="/contact" className="text-[#00594C] font-semibold underline hover:text-[#99DF63] transition-colors">
        book a loan review
      </Link>{' '}
      with one of our brokers. There is no obligation, and the consultation
      will give you a clear picture of your options.
    </p>
  </BlogArticleTemplate>
);

export default WhenToRefinance;
